import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import './RokuSettings.css'

// How many ids per /images/migrate call. Each item does a remote CF Images
// upload (~1-2s), so keep chunks small to stay within Worker time limits.
const CHUNK = 4

function fmtDate(s) {
  if (!s) return ''
  const d = new Date(String(s).replace(' ', 'T') + 'Z') // stored as UTC
  return isNaN(d) ? s : d.toLocaleString()
}

// Inline panel (formerly the Image Manager "ComplianceModal"): surface VOD items with
// YouTube-hosted thumbnails and migrate them to Cloudflare Images (16:9).
export default function RokuImageMigration() {
  const { api } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [checked, setChecked] = useState(new Set())
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [statusById, setStatusById] = useState({}) // tbtv_id -> { ok, newUrl?, error? }
  const [tab, setTab] = useState('pending') // 'pending' | 'history'
  const [tagFilter, setTagFilter] = useState('')
  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const data = await api('/images/migration-history?limit=300')
      setHistory(data.items || [])
    } catch {
      setHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }, [api])

  useEffect(() => { if (tab === 'history') loadHistory() }, [tab, loadHistory])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await api('/images/compliance')
      setItems(data.items || [])
      setChecked(new Set())
      setStatusById({})
    } catch (e) {
      setError(e.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  function toggle(id) {
    if (processing) return
    setChecked(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (processing) return
    const vis = (tagFilter ? items.filter(i => (i.tags || []).includes(tagFilter)) : items)
      .filter(i => !statusById[i.tbtv_id]?.ok)
      .map(i => i.tbtv_id)
    const allChecked = vis.length > 0 && vis.every(id => checked.has(id))
    setChecked(prev => {
      const next = new Set(prev)
      if (allChecked) vis.forEach(id => next.delete(id))
      else vis.forEach(id => next.add(id))
      return next
    })
  }

  async function migrateSelected() {
    const ids = [...checked].filter(id => !statusById[id]?.ok)
    if (!ids.length) return
    setProcessing(true)
    setProgress({ done: 0, total: ids.length })

    let done = 0
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK)
      try {
        const resp = await api('/images/migrate', { method: 'POST', body: JSON.stringify({ ids: chunk }) })
        const map = {}
        for (const r of (resp.results || [])) map[r.tbtv_id] = { ok: !!r.ok, newUrl: r.newUrl, error: r.error }
        setStatusById(prev => ({ ...prev, ...map }))
      } catch (e) {
        const map = {}
        for (const id of chunk) map[id] = { ok: false, error: e.message || 'request failed' }
        setStatusById(prev => ({ ...prev, ...map }))
      }
      done += chunk.length
      setProgress({ done, total: ids.length })
    }

    setProcessing(false)
    setChecked(new Set())
    loadHistory()
  }

  const allTags = [...new Set(items.flatMap(i => i.tags || []))].sort()
  const visibleItems = tagFilter ? items.filter(i => (i.tags || []).includes(tagFilter)) : items
  const pending = visibleItems.filter(i => !statusById[i.tbtv_id]?.ok)
  const migratedCount = items.filter(i => statusById[i.tbtv_id]?.ok).length
  const selectedCount = [...checked].filter(id => !statusById[id]?.ok).length

  return (
    <div className="cmpl-panel">
      <div className="cmpl-tabs">
        <button className={`cmpl-tab${tab === 'pending' ? ' active' : ''}`} onClick={() => setTab('pending')} disabled={processing}>
          Needs migration{items.length ? ` (${pending.length})` : ''}
        </button>
        <button className={`cmpl-tab${tab === 'history' ? ' active' : ''}`} onClick={() => setTab('history')} disabled={processing}>
          History{history.length ? ` (${history.length})` : ''}
        </button>
      </div>

      {tab === 'pending' && (
        <div className="cmpl-subhead">
          <p>
            These VOD items use a YouTube-hosted thumbnail (out of compliance — they render inconsistently
            and fail Roku's 16:9 image requirement). Select items and migrate them to Cloudflare Images
            (16:9). The catalog updates in place; all apps pick up the new image.
          </p>
          <div className="cmpl-toolbar">
            <button className="cmpl-btn" onClick={load} disabled={loading || processing}>
              <RefreshCw size={14} /> Refresh
            </button>
            <select className="cmpl-select" value={tagFilter} onChange={e => setTagFilter(e.target.value)}
              disabled={loading || processing || !allTags.length} title="Filter by tag">
              <option value="">All tags ({items.length})</option>
              {allTags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button className="cmpl-btn" onClick={toggleAll} disabled={loading || processing || !pending.length}>
              {pending.length > 0 && pending.every(i => checked.has(i.tbtv_id)) ? 'Clear all' : 'Select all'}
            </button>
            <span className="cmpl-spacer" />
            <span className="cmpl-count">
              {tagFilter ? `${visibleItems.length} of ${items.length}` : `${items.length} flagged`}
              {migratedCount ? ` · ${migratedCount} migrated` : ''}
            </span>
            <button className="cmpl-btn cmpl-btn-primary" onClick={migrateSelected} disabled={processing || selectedCount === 0}>
              {processing ? `Migrating ${progress.done}/${progress.total}…` : `Migrate ${selectedCount || ''} to Cloudflare`}
            </button>
          </div>
        </div>
      )}

      <div className="cmpl-body">
        {tab === 'pending' ? (
          <>
            {loading && <div className="cmpl-msg">Loading…</div>}
            {error && <div className="cmpl-msg cmpl-error"><AlertTriangle size={16} /> {error}</div>}
            {!loading && !error && items.length === 0 && (
              <div className="cmpl-msg cmpl-ok"><CheckCircle2 size={16} /> No YouTube thumbnails found — all compliant.</div>
            )}
            {!loading && !error && items.length > 0 && visibleItems.length === 0 && (
              <div className="cmpl-msg">No items with tag “{tagFilter}”.</div>
            )}
            <div className="cmpl-grid">
              {visibleItems.map(item => {
                const st = statusById[item.tbtv_id]
                const isChecked = checked.has(item.tbtv_id)
                return (
                  <div key={item.tbtv_id}
                    className={`cmpl-card${st?.ok ? ' done' : ''}${st && !st.ok ? ' failed' : ''}${isChecked ? ' checked' : ''}`}
                    onClick={() => !st?.ok && toggle(item.tbtv_id)}>
                    <div className="cmpl-thumb">
                      <img src={st?.ok ? st.newUrl : item.current_url} alt={item.title} loading="lazy"
                        onError={e => { e.currentTarget.style.visibility = 'hidden' }} />
                      {!st?.ok && (
                        <input type="checkbox" className="cmpl-check" checked={isChecked}
                          onChange={() => toggle(item.tbtv_id)} onClick={e => e.stopPropagation()} disabled={processing} />
                      )}
                      {st?.ok && <span className="cmpl-badge ok"><CheckCircle2 size={14} /> Migrated</span>}
                      {st && !st.ok && <span className="cmpl-badge err" title={st.error}><AlertTriangle size={14} /> Failed</span>}
                    </div>
                    <div className="cmpl-meta">
                      <div className="cmpl-name" title={item.title}>{item.title}</div>
                      <div className="cmpl-sub">{item.status || ''}{item.status ? ' · ' : ''}{item.tbtv_id}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        ) : (
          <>
            {historyLoading && <div className="cmpl-msg">Loading history…</div>}
            {!historyLoading && history.length === 0 && <div className="cmpl-msg">No migrations yet.</div>}
            {history.length > 0 && (
              <div className="cmpl-history">
                {history.map(h => (
                  <div key={h.id} className="cmpl-hrow">
                    <img className="cmpl-hthumb old" src={h.old_url} alt="before" loading="lazy"
                      title="Before (YouTube)" onError={e => { e.currentTarget.style.visibility = 'hidden' }} />
                    <ArrowRight size={16} className="cmpl-harrow" />
                    <img className="cmpl-hthumb new" src={h.new_url} alt="after" loading="lazy"
                      title="After (Cloudflare 16:9)" onError={e => { e.currentTarget.style.visibility = 'hidden' }} />
                    <div className="cmpl-hmeta">
                      <div className="cmpl-name" title={h.title || h.tbtv_id}>{h.title || h.tbtv_id}</div>
                      <div className="cmpl-sub">{h.tbtv_id} · {fmtDate(h.migrated_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
